import { useEffect, useState } from "react";
import { Loader2, Sparkles, FileText, FileSpreadsheet, ClipboardList, FileCheck, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../api/client";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const TYPE_GROUPS = [
  { id: "policy", label: "Políticas", icon: FileText },
  { id: "procedure", label: "Procedimientos", icon: ClipboardList },
  { id: "record", label: "Registros", icon: FileSpreadsheet },
  { id: "plan", label: "Planes", icon: FileText },
  { id: "soa", label: "Declaración de Aplicabilidad", icon: FileCheck },
];

export default function NewFromTemplateModal({ open, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingCode, setGeneratingCode] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/document-templates");
        setTemplates(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const grouped = TYPE_GROUPS.map((g) => ({ ...g, items: templates.filter((t) => t.type === g.id) }));

  const generate = async (tpl) => {
    setGeneratingCode(tpl.code);
    try {
      const { data } = await api.post("/documents/from-template", { template_code: tpl.code });
      toast.success(tpl.code === "soa_statement_of_applicability" ? "SoA generado." : "Documento generado con IA.");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo generar el documento.");
    } finally { setGeneratingCode(null); }
  };

  const isSoa = (c) => c === "soa_statement_of_applicability";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !generatingCode && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="modal-new-from-template">
        <DialogHeader>
          <DialogTitle>Nuevo documento desde plantilla</DialogTitle>
          <DialogDescription>15 plantillas núcleo del SGSI. La generación con IA tarda 20-40 segundos.</DialogDescription>
        </DialogHeader>

        {loading && <div className="text-slate-500">Cargando plantillas...</div>}

        {!loading && grouped.map((g) => g.items.length > 0 && (
          <div key={g.id} className="mt-3">
            <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1">
              <g.icon className="h-3 w-3" /> {g.label}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {g.items.map((t) => {
                const busy = generatingCode === t.code;
                return (
                  <div key={t.code} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`tpl-${t.code}`}>
                    <div className="font-semibold text-slate-900 text-[14px]">{t.title}</div>
                    <p className="text-[12px] text-slate-500 mt-1 line-clamp-2">{t.description}</p>
                    <div className="text-[11px] text-slate-400 mt-2">Cubre {t.default_related_controls.length} controles</div>
                    <Button
                      size="sm" className="mt-3 w-full" disabled={!!generatingCode}
                      onClick={() => generate(t)} data-testid={`tpl-generate-${t.code}`}
                    >
                      {busy ? (<><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generando...</>) : (
                        isSoa(t.code) ? (<><RefreshCw className="h-3 w-3 mr-1" /> Regenerar SoA</>) : (<><Sparkles className="h-3 w-3 mr-1" /> Generar con IA</>)
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {generatingCode && !isSoa(generatingCode) && (
          <div className="text-[12px] text-slate-500 mt-4 text-center">
            Generando documento con IA... (esto puede tardar 20-40 segundos)
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
